import { Button, Card, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

const steps = [
  {
    title: 'Create your league',
    description: 'Start a private competition for your crew with its own draft, scoring, and season rhythm.',
  },
  {
    title: 'Draft real runners',
    description: 'Turn your league into a fantasy-style competition where every athlete workout can move the standings.',
  },
  {
    title: 'Sync activity and score the season',
    description: 'Connect Garmin or Strava so mileage and points keep rolling in.',
  },
];

const ownerRules = [
  {
    title: 'You set the format',
    description: 'Launch a private league for your group and choose whether the season is a total-points race or a weekly head-to-head battle.',
  },
  {
    title: 'Draft night decides the roster',
    description: 'Run a live snake draft, build out every team, and let League Runner handle the pace so the room stays moving.',
  },
  {
    title: 'Real training drives results',
    description: 'Every synced workout can impact the standings, which means your league stays active because the scoring comes from real miles.',
  },
  {
    title: 'Invites stay simple',
    description: 'Share invites, fill the league with friends, and keep membership under control from one owner dashboard.',
  },
];

const GetStartedScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const openLeagueIntent = (intent) => {
    if (userInfo) {
      navigate(`/leagues?intent=${intent}`);
      return;
    }

    navigate(`/register?intent=${intent}`);
  };

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <div className="hero-shell mb-5">
            <div className="hero-banner">
              <div className="hero-content-panel">
                <div className="hero-kicker">Get Started</div>
                <h1 className="hero-title">You're about to really love running</h1>
                <p className="hero-subtitle">
                  League Runner turns your miles into points for your team. Draft your friends, track your workouts, and get amped to put down some miles.
                </p>
                <div className="hero-actions">
                  <Button className="btn btn-primary-modern" onClick={() => openLeagueIntent('create-league')}>
                    Create a League
                  </Button>
                  <Button className="btn btn-outline-modern" onClick={() => openLeagueIntent('join-league')}>
                    Join a League
                  </Button>
                </div>
                {!userInfo && (
                  <p className="mt-3 mb-0" style={{ color: 'rgba(255, 253, 250, 0.88)' }}>
                    Already have an account? <Link to="/login" className="text-decoration-none fw-medium" style={{ color: '#ffffff' }}>Sign in</Link>
                  </p>
                )}
              </div>
            </div>
          </div>

          <Row className="g-4 mb-5">
            {steps.map((step, index) => (
              <Col md={4} key={step.title}>
                <Card className="modern-card h-100">
                  <div className="modern-card-header">
                    <div className="text-primary fw-semibold mb-2">0{index + 1}</div>
                    <h2 className="modern-card-title">{step.title}</h2>
                    <p className="modern-card-subtitle">{step.description}</p>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="modern-card">
            <div className="modern-card-header">
              <h2 className="modern-card-title">What the heck is this?</h2>
              <p className="modern-card-subtitle">The fantasy league where you are the athlete.</p>
            </div>
            <Card.Body>
              <Row className="g-4">
                <Col md={6}>
                  <h3 className="h5 text-primary">For league creators</h3>
                  <p className="text-secondary mb-0">
                    Spin up a league, invite your friends, host a live draft, and start running.
                  </p>
                </Col>
                <Col md={6}>
                  <h3 className="h5 text-primary">For athletes and team managers</h3>
                  <p className="text-secondary mb-0">
                    Connect Garmin or Strava, let activity sync do the heavy lifting, and dominate the competition. Your miles multiply your team points.
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="modern-card mt-4">
            <div className="modern-card-header">
              <h2 className="modern-card-title">League rules, from the owner chair</h2>
              <p className="modern-card-subtitle">Everything you need to explain the format, set expectations, and make your league sound worth joining.</p>
            </div>
            <Card.Body>
              <p className="mb-4 text-secondary">
                League Runner gives you the structure of a fantasy league without losing the fun of real-world running. As the league owner, you choose the format, run the draft, invite the group, and let the season play out through the workouts your members are already doing.
              </p>
              <Row className="g-4">
                {ownerRules.map((rule) => (
                  <Col md={6} key={rule.title}>
                    <h3 className="h5 text-primary">{rule.title}</h3>
                    <p className="text-secondary mb-0">{rule.description}</p>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Card className="modern-card mt-4">
            <div className="modern-card-header">
              <h2 className="modern-card-title">Ad-free plans are ready when you are</h2>
              <p className="modern-card-subtitle">Choose a recurring or lifetime upgrade after you create your account.</p>
            </div>
            <Card.Body>
              <p className="mb-3">
                League Runner supports monthly and one-time ad-free upgrades through Stripe. Create your account, head to your profile, and pick the plan that fits your league season.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button className="btn btn-primary-modern" onClick={() => navigate(userInfo ? '/profile' : '/register')}>
                  {userInfo ? 'Manage Billing' : 'Create Account'}
                </Button>
                {!userInfo && (
                  <Button className="btn btn-outline-modern" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GetStartedScreen;